// The blue brand slab shown beside every auth surface (via AuthShell, and
// directly by RequestAccessPage). Presentation only; only the copy varies
// between surfaces, so only copy is a prop.
import { motion, useReducedMotion } from 'framer-motion'

export default function AuthBrandPanel({ heading, subtitle, items = [] }) {
  const reduceMotion = useReducedMotion()

  // One-shot rise, never a loop — DESIGN_SYSTEM §2.6: motion is "felt, not
  // watched", may "wipe in once on load", and "never loops". These two blobs
  // ran `repeat: Infinity` at 7s/8s, which is a permanent idle animation on a
  // login screen.
  const rise = (from, delay) =>
    reduceMotion
      ? {}
      : {
          initial: { y: from, opacity: 0 },
          animate: { y: 0, opacity: 1 },
          transition: { duration: 0.7, ease: 'easeOut', delay },
        }

  return (
    // Flush to the top/left/bottom viewport edges, rounded only on the right
    // corners — a slab cut into the page. Hidden below `lg` so the form owns
    // the fold on mobile; callers use `lg:order-1` and keep the form first
    // in the DOM so tab order is unaffected.
    <div className="hidden lg:flex lg:w-[42%] shrink-0 lg:order-1">
      <div className="relative w-full overflow-hidden rounded-r-3xl rounded-l-none bg-hero-gradient">
        <div className="absolute inset-0 bg-mesh-dots opacity-[0.15]" aria-hidden="true" />

        {/* Scrim — load-bearing for WCAG AA. `hero-gradient` runs at 160deg, so
            combined with this vertical scrim and the glow blob below, the
            lightest rendered zone is top-right, not the bottom — measure there.
            At 10/20/35 the body copy is ~6.6:1 and the caption ~5.9:1. Keep
            small text on this panel at /85 opacity or above. */}
        <div
          className="absolute inset-0 bg-gradient-to-b from-black/10 via-black/20 to-black/35"
          aria-hidden="true"
        />

        {/* Both blobs are white. The lower one was `bg-teal-400/10`, which
            §3.8 bans outright ("no decorative teal" — teal survives only as
            the vegetation ramp or a `risk-normal` fill). Two stops of the same
            white also read as one light source rather than two competing
            tints, which is the point of the slab. */}
        <motion.div
          aria-hidden="true"
          className="absolute -top-24 -right-20 w-80 h-80 rounded-full bg-white/10 blur-3xl"
          {...rise(-24, 0)}
        />
        <motion.div
          aria-hidden="true"
          className="absolute bottom-0 -left-16 w-64 h-64 rounded-full bg-white/5 blur-3xl"
          {...rise(20, 0.1)}
        />

        {/* `lg:overflow-y-auto` mirrors the form column in AuthShell.jsx:24.
            The slab wrapper above is `overflow-hidden` (it has to be — the
            blur blobs sit at negative offsets and the rounded-r-3xl corner
            clips through it), and AuthShell's row is `lg:overflow-hidden`, so
            without a scroll grant somewhere on this side the panel amputates
            silently instead of scrolling. It belongs here, on the content
            column, which is the only box with no negatively-offset children
            to turn into phantom scroll extent.
            `short:` trims ~92px of information-free gap (py 40→24, text mt
            32→16, list mt 40→24, list gap 16→12, ministry pt 40→24). Gated at
            max-height:800px on purpose, not at the measured clip onset — a
            literal at the onset is a knife-edge that only fires at the one
            height it was measured against. */}
        <div className="relative z-10 flex flex-col h-full px-8 py-8 lg:px-10 lg:py-10 short:py-6 lg:overflow-y-auto">
          {/* Client asked for the mark moved and set as large as the panel
              allows (01 Sep 2026); it was a small left-aligned pill at h-20.
              The asset itself was the first constraint — the source PNG carried
              ~70px of transparent margin per side inside a 1000x666 canvas, so
              a "full width" plate spent a fifth of its width on nothing. It is
              now cropped to its ink box (863x454, aspect 1.90) and the plate
              padding is the only margin, hence p-3 rather than the old
              px-5 py-4.
              A fixed max-h cap was tried and measured wrong: a design review
              found the ministry line still clipping at 1024x700 both with the
              cap (+30.3px) and without it (+40.8px). A viewport-unit cap cannot
              know how tall the text below actually is. So the height bound is
              the layout itself:
              the text block below takes its natural height (shrink-0) and the
              plate is the only item that gives, so on a short viewport the
              spare pixels come out of the logo and nothing overflows, while on
              a tall one the plate stays exactly its content height instead of
              growing into a white slab.
              Re-measured 04 Sep 2026, after a FOURTH bullet was added to
              authBrandCopy.js on client instruction. Rendered headless at
              1024x700 and 1024x600: the ministry line is complete at both, and
              the plate is visibly the item that gave. The clipping recorded
              above is history, not current behaviour — the `short:` variant and
              the scroll grant below it both postdate that review. Read it as
              why the max-h cap was rejected, not as a live defect. */}
          {/* w-fit, not w-full. With w-full the plate spans the column and
              object-contain letterboxes the mark INSIDE it, so the spare width
              paints as white plate rather than disappearing: measured 145px and
              140px of dead white flanking the shield at 1920x600 — 38% of the
              plate — and 26.2% at 1440x600. It reads as a blank banner with a
              logo parked in it. w-fit shrinks the plate to the painted picture,
              which removes the white WITHOUT changing the rendered mark size:
              verified identical painted dimensions across all 54 viewport cells.
              self-center, not self-start: self-start put the plate's left edge on
              the same x as the h1 and the ministry line, which is the tidier
              rule, but at this size it reads as a plate shoved into the corner
              with a long empty run of blue to its right. Client preferred it
              centred on sight (03 Sep 2026). Centring costs the shared left axis
              and buys a symmetrical mark over a left-aligned text block below —
              a deliberate trade, not an oversight. The text block keeps its own
              alignment; only the plate moves. */}
          {/* The WIDTH bound is min(100%, 24rem), and it is the one that was
              missing. The vertical rules above bound how much the plate may
              yield on a short viewport; nothing bounded how wide it could get
              on a normal one, so a bare `max-w-full` let the 863px mark scale
              to the whole column — measured 682px painted inside a 765px panel,
              89% of the width. At that size a white plate on the blue ground is
              the loudest element on the page: it outweighs the h1 and the
              sign-in card. Client feedback asked for the mark "as large as
              possible", which is what produced that; large enough to read as
              the system's identity is the actual intent, and 24rem does it
              while leaving the headline the primary voice (measured 64% of the
              column at 1512, 51% at 1920, h1 wider than the plate at both).

              Both bounds have to be ONE declaration. Stacking `max-w-full` and
              `max-w-sm` looks equivalent and is not: they are two rules on the
              same property, so the generated sheet's source order decides which
              wins, not the class order. It resolved to the absolute 24rem and
              at 1024x700 — where the column leaves 350px — the mark overflowed
              its own plate and the slab's `overflow-hidden` sheared the "S" off
              IEWRS. 24rem is the `sm` scale value, not an off-scale literal.

              The cap goes on the img, not the plate: the plate is w-fit, so
              constraining the picture shrinks the white with it and preserves
              the no-dead-white property the w-fit comment above documents.
              Putting it on a wrapper instead would make the plate stop being
              the column's yielding flex item and break the height contract
              documented above. */}
          <span className="flex min-h-0 w-fit max-w-[min(100%,24rem)] self-center items-center justify-center rounded-2xl bg-white p-3 shadow-sm">
            <img
              src="/iewrs-logo.png?v=4"
              alt="IEWRS — Integrated Early Warning & Response System"
              width="863"
              height="454"
              className="block h-auto w-auto max-h-full max-w-[min(100%,24rem)] object-contain aspect-[863/454]"
            />
          </span>

          {/* flex-auto + shrink-0, not flex-1: with a 0 basis this block's real
              content height was invisible to the column, so the free space the
              logo plate claimed was computed as if the text took none and the
              ministry line below fell off the bottom. Counting it as a basis
              that never shrinks is what makes the plate above the yielding
              item. */}
          <div className="flex flex-col justify-center flex-auto shrink-0 mt-8 short:mt-4">
            <h1 className="text-2xl xl:text-3xl font-bold tracking-tight text-white text-balance">
              {heading}
            </h1>
            <p className="mt-4 text-sm leading-relaxed text-white/95 text-balance">
              {subtitle}
            </p>

            <ul className="mt-10 space-y-4 short:mt-6 short:space-y-3">
              {items.map(({ icon: Icon, label }) => (
                <li key={label} className="flex items-start gap-3 text-sm text-white/95">
                  <span className="flex items-center justify-center w-8 h-8 rounded-2xl bg-white/10 ring-1 ring-white/15 shrink-0">
                    <Icon className="w-4 h-4 text-white" strokeWidth={1.75} aria-hidden="true" />
                  </span>
                  <span className="pt-1.5">{label}</span>
                </li>
              ))}
            </ul>
          </div>

          <p className="text-xs text-white/85 mt-auto pt-10 short:pt-6 shrink-0">
            Ministry of Health &middot; Rwanda Biomedical Centre &middot; Rwanda Space Agency
          </p>
        </div>
      </div>
    </div>
  )
}
